import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LineChart,
  Line,
  Area,
  Cell,
} from "recharts";
import {
  Users,
  TrendingUp,
  Activity,
  AlertTriangle,
  Camera,
  Clock,
  MapPin,
  Loader2,
} from "lucide-react";
import axios from "../../utils/axiosDash";

// ============================================================
// LABEL COMPONENT
// ============================================================

const Label = ({ children, className = "" }) => (
  <span
    className={`text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 ${className}`}
  >
    {children}
  </span>
);

// ============================================================
// HELPERS
// ============================================================

const getSeverityStyle = (severity) => {
  switch (severity) {
    case "Critical":
      return {
        color: "text-red-700",
        background: "bg-red-50",
        border: "border-red-100",
      };
    case "High":
      return {
        color: "text-amber-700",
        background: "bg-amber-50",
        border: "border-amber-100",
      };
    case "Moderate":
      return {
        color: "text-blue-700",
        background: "bg-blue-50",
        border: "border-blue-100",
      };
    default:
      return {
        color: "text-emerald-700",
        background: "bg-emerald-50",
        border: "border-emerald-100",
      };
  }
};

// Status is now derived from a camera's reading relative to the busiest
// reading observed across all cameras in the current dataset, since the
// backend does not provide a fixed per-camera capacity.
const getStatusFromRelativeLoad = (percentage) => {
  if (percentage >= 85) {
    return {
      label: "Critical",
      color: "text-red-700",
      bg: "bg-red-50",
      border: "border-red-100",
    };
  }

  if (percentage >= 60) {
    return {
      label: "High",
      color: "text-amber-700",
      bg: "bg-amber-50",
      border: "border-amber-100",
    };
  }

  if (percentage >= 35) {
    return {
      label: "Moderate",
      color: "text-blue-700",
      bg: "bg-blue-50",
      border: "border-blue-100",
    };
  }

  return {
    label: "Normal",
    color: "text-emerald-700",
    bg: "bg-emerald-50",
    border: "border-emerald-100",
  };
};

const getOccupancyColor = (percentage) => {
  if (percentage >= 80) return "text-red-600";
  if (percentage >= 60) return "text-amber-600";
  if (percentage >= 40) return "text-blue-600";
  return "text-emerald-600";
};

const getOccupancyBarColor = (percentage) => {
  if (percentage >= 80) return "bg-red-500";
  if (percentage >= 60) return "bg-amber-500";
  if (percentage >= 40) return "bg-blue-500";
  return "bg-emerald-500";
};

const BAR_RANK_COLORS = ["#EF4444", "#8B5CF6", "#3B82F6", "#94A3B8", "#94A3B8", "#94A3B8", "#94A3B8"];

// ============================================================
// CUSTOM TOOLTIP
// ============================================================

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div className="rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 shadow-xl">
      <p className="mb-1 text-[10px] font-medium text-slate-400">{label}</p>
      <p className="text-sm font-bold text-white">
        {payload[0].value} people
      </p>
    </div>
  );
};

// ============================================================
// KPI CARD
// ============================================================

const KpiCard = ({
  label,
  value,
  description,
  trend,
  trendIcon: TrendIcon = TrendingUp,
  trendClassName,
  icon: Icon,
  iconClassName,
  iconBackground,
}) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <Label>{label}</Label>
        <p className="mt-2 text-3xl font-bold tracking-tight text-slate-950">
          {value}
        </p>
        <p className="mt-1 text-xs text-slate-500">{description}</p>
      </div>

      <div
        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${iconBackground}`}
      >
        <Icon size={19} className={iconClassName} strokeWidth={2} />
      </div>
    </div>

    <div
      className={`mt-4 flex items-center gap-1.5 text-[11px] font-semibold ${trendClassName}`}
    >
      <TrendIcon size={13} />
      <span>{trend}</span>
    </div>
  </div>
);

// ============================================================
// SECTION HEADER
// ============================================================

const SectionHeader = ({ eyebrow, title, description, rightContent }) => (
  <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
    <div>
      {eyebrow && <Label>{eyebrow}</Label>}
      <h2 className="mt-1 text-sm font-bold text-slate-950">{title}</h2>
      {description && (
        <p className="mt-1 text-xs text-slate-500">{description}</p>
      )}
    </div>

    {rightContent}
  </div>
);

// ============================================================
// EMPTY STATE
// ============================================================

const EmptyState = ({ label }) => (
  <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
    <Activity size={22} className="text-slate-300" />
    <p className="text-xs font-medium text-slate-400">{label}</p>
  </div>
);

// ============================================================
// MAIN COMPONENT
// ============================================================

const CrowdAnalytics = () => {
  const [prestigeCrowdData, setPrestigeCrowdData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPrestigeCrowdData();

    const interval = setInterval(() => {
      fetchPrestigeCrowdData();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const fetchPrestigeCrowdData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.post("/fetch-prestige-crowd");

      if (response.data?.success) {
        // Backend already returns processed analytics
        setPrestigeCrowdData(response.data.data);
      } else {
        setPrestigeCrowdData(null);
      }
    } catch (err) {
      console.error("Error fetching Prestige Crowd data:", err);
      setError("Unable to load crowd analytics right now.");
      setPrestigeCrowdData(null);
    } finally {
      setLoading(false);
    }
  };

  // ----------------------------------------------------------
  // Derive every metric, chart series and table row from the
  // raw records returned by the backend. No hardcoded/demo data.
  // Each record looks like:
  // { camera_id, camera_ip, camera_name, person_count, timestamp, createdAt, _id }
  // ----------------------------------------------------------
  const processed = useMemo(() => {
    if (!prestigeCrowdData) {
      return {
        currentOccupancy: 0,
        peakOccupancy: 0,
        avgOccupancy: 0,
        peakTimeLabel: "—",
        cameraCount: 0,
        peopleByHour: [],
        cameraOccupancy: [],
        hotZones: [],
        weeklyPattern: [],
        busiestDayLabel: null,
        peakEvents: [],
        cameraStatus: [],
        lastUpdated: null,
      };
    }

    return {
      currentOccupancy: prestigeCrowdData.currentOccupancy ?? 0,
      peakOccupancy: prestigeCrowdData.peakOccupancy ?? 0,

      // Backend calls this averageOccupancy
      avgOccupancy: prestigeCrowdData.averageOccupancy ?? 0,

      // Backend gives peakTime as ISO timestamp
      peakTimeLabel: prestigeCrowdData.peakTime
        ? new Date(prestigeCrowdData.peakTime).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
          })
        : "—",

      cameraCount: prestigeCrowdData.cameraCount ?? 0,

      peopleByHour: Array.isArray(prestigeCrowdData.peopleByHour)
        ? prestigeCrowdData.peopleByHour
        : [],

      cameraOccupancy: Array.isArray(prestigeCrowdData.cameraOccupancy)
        ? prestigeCrowdData.cameraOccupancy
        : [],

      hotZones: Array.isArray(prestigeCrowdData.hotZones)
        ? prestigeCrowdData.hotZones
        : [],

      weeklyPattern: Array.isArray(prestigeCrowdData.weeklyPattern)
        ? prestigeCrowdData.weeklyPattern
        : [],

      busiestDayLabel: prestigeCrowdData.busiestDayLabel ?? null,

      peakEvents: Array.isArray(prestigeCrowdData.peakEvents)
        ? prestigeCrowdData.peakEvents
        : [],

      cameraStatus: Array.isArray(prestigeCrowdData.cameraStatus)
        ? prestigeCrowdData.cameraStatus
        : [],

      lastUpdated: prestigeCrowdData.lastUpdated
        ? new Date(prestigeCrowdData.lastUpdated)
        : null,
    };
  }, [prestigeCrowdData]);

  const hasData =
    prestigeCrowdData &&
    typeof prestigeCrowdData === "object";

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-5 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-[1600px] flex-col gap-6">
        {/* Header */}
        <header className="flex flex-wrap items-end justify-between gap-5">
          <div>
            <div className="mb-2 flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-violet-600" />
              <Label className="text-violet-600">Live Analytics</Label>
            </div>

            <h1 className="text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
              Crowd Analytics
            </h1>

            <p className="mt-2 max-w-2xl text-sm text-slate-500">
              Monitor campus occupancy, crowd density, movement patterns, and
              high-traffic areas in real time.
            </p>
          </div>

          <div className="flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-xs font-bold text-emerald-700">
            {loading ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
            )}
            {loading ? "LOADING" : "LIVE MONITORING"}
          </div>
        </header>

        {error && (
          <div className="flex items-center gap-2 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-xs font-medium text-red-700">
            <AlertTriangle size={14} />
            {error}
          </div>
        )}

        {/* KPI Cards */}
        <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <KpiCard
            label="Current Occupancy"
            value={processed.currentOccupancy}
            description="People detected across all cameras"
            trend={`${processed.cameraCount} camera${
              processed.cameraCount === 1 ? "" : "s"
            } reporting`}
            trendIcon={Camera}
            trendClassName="text-slate-500"
            icon={Users}
            iconClassName="text-blue-600"
            iconBackground="bg-blue-50"
          />

          <KpiCard
            label="Peak Occupancy"
            value={processed.peakOccupancy}
            description="Highest hourly total in this dataset"
            trend={`Peak at ${processed.peakTimeLabel}`}
            trendIcon={Clock}
            trendClassName="text-amber-600"
            icon={TrendingUp}
            iconClassName="text-violet-600"
            iconBackground="bg-violet-50"
          />

          <KpiCard
            label="Average Occupancy"
            value={processed.avgOccupancy}
            description="Average people per hour tracked"
            trend={`Across ${processed.peopleByHour.length} hour${
              processed.peopleByHour.length === 1 ? "" : "s"
            }`}
            trendIcon={Activity}
            trendClassName="text-emerald-600"
            icon={Activity}
            iconClassName="text-teal-600"
            iconBackground="bg-teal-50"
          />

          <KpiCard
            label="Crowd Events"
            value={processed.peakEvents.length}
            description="Notable readings surfaced from live data"
            trend={
              processed.busiestDayLabel
                ? `Busiest day: ${processed.busiestDayLabel}`
                : "No pattern yet"
            }
            trendIcon={AlertTriangle}
            trendClassName="text-red-600"
            icon={AlertTriangle}
            iconClassName="text-red-600"
            iconBackground="bg-red-50"
          />
        </section>

        {/* People on Campus Chart */}
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <SectionHeader
            eyebrow="Occupancy trend"
            title="People on campus by hour"
            description="Hourly campus population based on camera detections"
            rightContent={
              <div className="flex items-center gap-2 rounded-lg bg-violet-50 px-3 py-1.5 text-[10px] font-bold text-violet-700">
                <Users size={13} />
                PEOPLE COUNT
              </div>
            }
          />

          <div className="h-[320px] p-4 sm:p-6">
            {!hasData ? (
              <EmptyState label={loading ? "Loading occupancy data…" : "No occupancy data available"} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={processed.peopleByHour}
                  margin={{ top: 10, right: 20, left: -10, bottom: 5 }}
                >
                  <CartesianGrid
                    stroke="#e2e8f0"
                    vertical={false}
                    strokeDasharray="3 3"
                  />

                  <XAxis
                    dataKey="time"
                    tick={{
                      fontSize: 10,
                      fill: "#64748b",
                      fontWeight: 500,
                    }}
                    axisLine={false}
                    tickLine={false}
                    padding={{ left: 10, right: 10 }}
                  />

                  <YAxis
                    tick={{
                      fontSize: 10,
                      fill: "#64748b",
                      fontWeight: 500,
                    }}
                    axisLine={false}
                    tickLine={false}
                    width={40}
                  />

                  <Tooltip content={<CustomTooltip />} />

                  <defs>
                    <linearGradient
                      id="peopleGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
                    </linearGradient>
                  </defs>

                  <Area
                    type="monotone"
                    dataKey="people"
                    stroke="#8B5CF6"
                    strokeWidth={2}
                    fill="url(#peopleGradient)"
                    dot={false}
                    activeDot={false}
                  />

                  <Line
                    type="monotone"
                    dataKey="people"
                    stroke="#8B5CF6"
                    strokeWidth={3}
                    dot={{
                      fill: "#8B5CF6",
                      stroke: "#fff",
                      strokeWidth: 2,
                      r: 5,
                    }}
                    activeDot={{
                      fill: "#8B5CF6",
                      stroke: "#fff",
                      strokeWidth: 2,
                      r: 7,
                    }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </section>

        {/* Camera Occupancy and Hot Zones */}
        <section className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {/* Occupancy by Camera */}
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <SectionHeader
              eyebrow="Camera distribution"
              title="Occupancy by camera"
              description="Current count per camera, relative to the busiest reading"
              rightContent={
                <span className="rounded-full border border-blue-100 bg-blue-50 px-3 py-1.5 text-[10px] font-bold text-blue-700">
                  LIVE
                </span>
              }
            />

            <div className="flex flex-col gap-5 p-5">
              {!hasData ? (
                <EmptyState label={loading ? "Loading cameras…" : "No camera data available"} />
              ) : (
                processed.cameraOccupancy.map((item) => (
                  <div key={item.cameraId}>
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-2">
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-violet-50">
                          <Camera size={14} className="text-violet-600" />
                        </div>

                        <span className="truncate text-xs font-semibold text-slate-900">
                          {item.name}
                        </span>

                        <span className="shrink-0 text-[10px] text-slate-400">
                          {item.people} people
                        </span>
                      </div>

                      <span
                        className={`text-xs font-bold ${getOccupancyColor(
                          item.occupancy
                        )}`}
                      >
                        {item.occupancy}%
                      </span>
                    </div>

                    <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                      <div
                        className={`h-full rounded-full transition-all ${getOccupancyBarColor(
                          item.occupancy
                        )}`}
                        style={{ width: `${item.occupancy}%` }}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Density Hot Zones */}
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <SectionHeader
              eyebrow="Crowd monitoring"
              title="Busiest cameras right now"
              description="Cameras ranked by latest people count"
              rightContent={
                <span className="rounded-full border border-red-100 bg-red-50 px-3 py-1.5 text-[10px] font-bold text-red-700">
                  HOT ZONES
                </span>
              }
            />

            <div className="p-5">
              {!hasData ? (
                <EmptyState label={loading ? "Loading zones…" : "No zone data available"} />
              ) : (
                <>
                  <div className="flex flex-col gap-1">
                    {processed.hotZones.map((item) => (
                      <div
                        key={item.cameraId}
                        className="flex items-center justify-between gap-4 rounded-xl px-2 py-3 transition-colors hover:bg-slate-50"
                      >
                        <div className="flex min-w-0 items-center gap-2">
                          <MapPin
                            size={15}
                            className={
                              item.alert ? "text-red-500" : "text-violet-500"
                            }
                          />

                          <span className="truncate text-xs font-medium text-slate-800">
                            {item.zone}
                          </span>
                        </div>

                        <div className="flex shrink-0 items-center gap-2">
                          <span
                            className={`text-sm font-bold ${
                              item.alert ? "text-red-600" : "text-slate-700"
                            }`}
                          >
                            {item.people}
                          </span>

                          <span className="text-[10px] text-slate-400">people</span>

                          {item.alert && (
                            <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 flex items-center gap-2 rounded-xl border border-amber-100 bg-amber-50 px-3 py-3 text-[11px] text-amber-800">
                    <AlertTriangle size={14} className="shrink-0 text-amber-600" />
                    <span className="font-medium">
                      Ranked relative to the busiest camera reading in this dataset
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>

        {/* Weekly Pattern */}
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <SectionHeader
            eyebrow="Historical pattern"
            title="Weekly peak occupancy pattern"
            description="Highest single reading recorded by day"
            rightContent={
              <div className="flex items-center gap-2 text-[10px] font-semibold text-slate-500">
                <TrendingUp size={13} className="text-violet-600" />
                DAILY PEAK
              </div>
            }
          />

          <div className="h-[300px] p-4 sm:p-6">
            {!hasData || processed.weeklyPattern.length === 0 ? (
              <EmptyState label={loading ? "Loading weekly pattern…" : "No weekly data available"} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={processed.weeklyPattern}
                  margin={{ top: 5, right: 10, left: -20, bottom: 0 }}
                >
                  <CartesianGrid
                    stroke="#e2e8f0"
                    vertical={false}
                    strokeDasharray="3 3"
                  />

                  <XAxis
                    dataKey="day"
                    tick={{ fontSize: 10, fill: "#64748b", fontWeight: 500 }}
                    axisLine={false}
                    tickLine={false}
                  />

                  <YAxis
                    tick={{ fontSize: 10, fill: "#64748b", fontWeight: 500 }}
                    axisLine={false}
                    tickLine={false}
                  />

                  <Tooltip
                    contentStyle={{
                      background: "#ffffff",
                      color: "#0f172a",
                      borderRadius: 10,
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 4px 12px rgba(15, 23, 42, 0.10)",
                    }}
                    labelStyle={{
                      color: "#0f172a",
                      fontWeight: 600,
                    }}
                    itemStyle={{
                      color: "#0f172a",
                    }}
                    formatter={(value) => [`${value} people`, "Peak Occupancy"]}
                  />

                  <Bar dataKey="peak" name="Peak Occupancy" radius={[6, 6, 0, 0]} barSize={34}>
                    {processed.weeklyPattern.map((entry) => (
                      <Cell
                        key={entry.day}
                        fill={BAR_RANK_COLORS[entry.rank] || "#94A3B8"}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {processed.busiestDayLabel && (
            <div className="flex items-start gap-3 border-t border-amber-100 bg-amber-50 px-5 py-4 text-xs text-amber-800">
              <AlertTriangle size={15} className="mt-0.5 shrink-0 text-amber-600" />
              <span className="font-medium leading-5">
                {processed.busiestDayLabel} shows the highest peak occupancy in this
                dataset.
              </span>
            </div>
          )}
        </section>

        {/* Peak Events and Camera Status */}
        <section className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {/* Peak Crowd Events */}
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <SectionHeader
              eyebrow="Event history"
              title="Peak crowd events"
              description="Highest individual readings recorded"
              rightContent={
                <span className="rounded-full border border-red-100 bg-red-50 px-3 py-1.5 text-[10px] font-bold text-red-700">
                  {processed.peakEvents.length} EVENTS
                </span>
              }
            />

            <div className="max-h-[420px] overflow-auto p-4">
              {!hasData ? (
                <EmptyState label={loading ? "Loading events…" : "No events recorded"} />
              ) : (
                <table className="w-full min-w-[620px] border-collapse">
                  <thead className="sticky top-0 bg-white">
                    <tr className="border-b border-slate-100">
                      {["Time", "Zone", "Camera", "Peak People", "Severity"].map(
                        (heading) => (
                          <th
                            key={heading}
                            className="px-2 py-3 text-left text-[9px] font-bold uppercase tracking-wider text-slate-400"
                          >
                            {heading}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>

                  <tbody>
                    {processed.peakEvents.map((event, index) => {
                      const severity = getSeverityStyle(event.severity);

                      return (
                        <tr
                          key={`${event.camera}-${index}`}
                          className="border-b border-slate-100 last:border-0 hover:bg-slate-50"
                        >
                          <td className="whitespace-nowrap px-2 py-3 text-[10px] text-slate-600">
                            <div className="flex items-center gap-1.5">
                              <Clock size={11} className="text-slate-400" />
                              {event.time}
                            </div>
                          </td>

                          <td className="whitespace-nowrap px-2 py-3 text-[11px] font-semibold text-slate-900">
                            <div className="flex items-center gap-1.5">
                              <MapPin size={11} className="text-violet-500" />
                              {event.zone}
                            </div>
                          </td>

                          <td className="px-2 py-3 text-[10px] font-medium text-slate-600">
                            {event.camera}
                          </td>

                          <td className="whitespace-nowrap px-2 py-3">
                            <span className="text-xs font-bold text-slate-900">
                              {event.peakPeople}
                            </span>
                            <span className="ml-1 text-[9px] text-slate-400">people</span>
                          </td>

                          <td className="px-2 py-3">
                            <span
                              className={`inline-flex rounded-full border px-2.5 py-1 text-[9px] font-bold ${severity.background} ${severity.color} ${severity.border}`}
                            >
                              {event.severity}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Camera-wise Crowd Status */}
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <SectionHeader
              eyebrow="Camera monitoring"
              title="Camera-wise crowd status"
              description="Latest reading per camera and relative load"
              rightContent={
                <div className="flex items-center gap-1.5 text-[10px] font-semibold text-slate-500">
                  <Camera size={13} />
                  {processed.cameraStatus.length} Cameras
                </div>
              }
            />

            <div className="overflow-x-auto p-4">
              {!hasData ? (
                <EmptyState label={loading ? "Loading cameras…" : "No camera data available"} />
              ) : (
                <table className="w-full min-w-[520px] border-collapse">
                  <thead>
                    <tr className="border-b border-slate-100">
                      {["Camera", "Location", "People", "Status"].map((heading) => (
                        <th
                          key={heading}
                          className="px-2 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400"
                        >
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {processed.cameraStatus.map((item) => {
                      const status = getStatusFromRelativeLoad(item.occupancy);

                      return (
                        <tr
                          key={item.cameraId}
                          className="border-b border-slate-100 last:border-0 hover:bg-slate-50"
                        >
                          <td className="px-2 py-3 text-[10px] font-bold text-slate-900">
                            {item.cameraId}
                          </td>

                          <td className="px-2 py-3 text-[10px] text-slate-600">
                            {item.name}
                          </td>

                          <td className="px-2 py-3 text-xs font-bold text-slate-900">
                            {item.people}
                          </td>

                          <td className="px-2 py-3">
                            <span
                              className={`inline-flex rounded-full border px-2.5 py-1 text-[9px] font-bold ${status.bg} ${status.color} ${status.border}`}
                            >
                              {status.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default CrowdAnalytics;